'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { BarChart3 } from 'lucide-react';
import type { ApiInitiative } from '@/lib/api';
import { REACTION_METADATA } from '@/lib/config/reactions';
interface InitiativeAnalyticsProps {
  initiative: ApiInitiative;
}

/**
 * Displays a simple reaction breakdown table from the initiative's engagement data.
 * Designed to be reusable — pass in the initiative record directly.
 */
export function InitiativeAnalytics(t0) {
  const $ = _c(15);
  const {
    initiative
  } = t0;
  let t1;
  bb0: {
    const byType = initiative.engagement?.reactions?.by_type;
    if (!byType || Object.keys(byType).length === 0) {
      let t2;
      if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = [];
        $[0] = t2;
      } else {
        t2 = $[0];
      }
      t1 = t2;
      break bb0;
    }
    let t2;
    if ($[1] !== byType) {
      t2 = Object.entries(byType).map(_temp).sort(_temp2);
      $[1] = byType;
      $[2] = t2;
    } else {
      t2 = $[2];
    }
    t1 = t2;
  }
  const rows = t1;
  const totalReactions = initiative.engagement?.reactions?.total ?? 0;
  if (totalReactions === 0) {
    let t2;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className="text-sm text-gray-500 text-center py-4">No reactions yet.</div>;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    return t2;
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <thead><tr className="border-b border-gray-200 text-left text-gray-500"><th className="pb-2 font-medium">Reaction</th><th className="pb-2 font-medium text-right">Count</th></tr></thead>;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== rows) {
    t3 = rows.map(_temp3);
    $[5] = rows;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = <tbody>{t3}</tbody>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <td className="pt-2 font-medium text-gray-700">Total</td>;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== totalReactions) {
    t6 = <tfoot><tr className="border-t border-gray-200">{t5}<td className="pt-2 text-right font-semibold text-gray-900">{totalReactions}</td></tr></tfoot>;
    $[10] = totalReactions;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t4 || $[13] !== t6) {
    t7 = <div><table className="w-full text-sm">{t2}{t4}{t6}</table></div>;
    $[12] = t4;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}
function _temp3(row) {
  return <tr key={row.type} className="border-b border-gray-50"><td className="py-2 flex items-center gap-2"><span>{row.emoji}</span><span className="text-gray-700">{row.label}</span></td><td className="py-2 text-right font-medium text-gray-900">{row.count}</td></tr>;
}
function _temp2(a, b) {
  return b.count - a.count;
}
function _temp(t0) {
  const [type, data] = t0;
  const meta = REACTION_METADATA[type];
  return {
    type,
    emoji: meta?.emoji ?? "\uD83D\uDC4D",
    label: meta?.label ?? type,
    color: meta?.chart_color ?? "#6B7280",
    count: data.count
  };
}